import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '../../-components/image';

/** Matches the `size-12` of the chip; used to size the CDN request. */
const LOGO_HEIGHT = 48;

/**
 * Wide landscape fallback, same as the portal header logo, for logos stored without
 * dimensions. Only feeds the CDN width, since the chip itself is a fixed square.
 */
const DEFAULT_LOGO_ASPECT_RATIO = 3;

function logoAspectRatio(logo: { width: number; height: number }) {
	return logo.width && logo.height ? logo.width / logo.height : DEFAULT_LOGO_ASPECT_RATIO;
}

/**
 * Mirrors the donors app's `CampaignNgos`
 * (`apps/donors/src/routes/-components/campaign-detail.tsx`): a small rounded logo chip
 * followed by the NGO name, sitting above the campaign title.
 *
 * Used on mobile in place of the "A campaign from:" card, which spends a lot of
 * vertical space on logos alone. Deviations from donors: the border token
 * (`border-border` instead of `border-border-alt`, which is the donors cream palette),
 * the use of the portal `Image` so the logo goes through the CDN like every other
 * portal image, and a larger padded chip so wordmark logos stay readable.
 */
export function CampaignNgosInline(props: {
	ngos: {
		id: string;
		name?: string;
		logo?: { location: string; width: number; height: number };
	}[];
	className?: string;
}) {
	const ngos = props.ngos
		.filter((ngo) => ngo.name ?? ngo.logo)
		// Same ordering as the desktop card so both views list the NGOs alike.
		// There is not a wide support for toSorted() in the browser
		// oxlint-disable-next-line unicorn/no-array-sort
		.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);

	if (ngos.length === 0) return null;

	return (
		<div className={cn('flex flex-wrap items-center gap-x-6 gap-y-2', props.className)}>
			{ngos.map((ngo) => (
				<div key={ngo.id} className="flex items-center gap-2">
					{ngo.logo ? (
						/* A square chip, but big enough (and padded) that a logo with a wordmark under
						   the emblem is still readable once `object-contain` letterboxes it inside. */
						<Image
							src={ngo.logo.location}
							alt={ngo.name ?? ''}
							height={LOGO_HEIGHT}
							aspectRatio={logoAspectRatio(ngo.logo)}
							objectFit="contain"
							className="border-border size-12 shrink-0 rounded-lg border bg-white object-contain! p-1"
						/>
					) : (
						<div className="border-border bg-secondary size-12 shrink-0 rounded-lg border" />
					)}
					<span className="font-medium">{ngo.name}</span>
				</div>
			))}
		</div>
	);
}
